import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CreateLogModal } from "components/Audit/CreateLogModal.jsx";
import RecordTable from "components/Records/RecordTable";
import ListSearchBar from "components/Records/ListSearchBar";
import ListPagination from "components/Records/ListPagination";
import RelativeTime from "components/RelativeTime";
import PageHeader from "components/PageLayout";
import { useAuditLogs } from "hooks/useAuditLogs";
import { useCreateAuditLog } from "hooks/useCreateAuditLog";
import { SearchProvider, useSearch } from "context/SearchContext";
import SearchPanel from "components/SearchPanel";
import { ChangeCategories, categoryBadgeClass } from "config/auditLogConfig";
import { toCsv, csvFilename, downloadCsv } from "utilities/csv";
import { useToast } from "components/Toast";
import { Filter, Plus, Download, ScrollText } from "lucide-react";

function AuditInner() {
  const { filters, set } = useSearch();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sort, setSort] = useState("-createdAt");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();

  const { logs, pagination, loading, error, refetch, setLogs } = useAuditLogs({
    filters,
    page,
    limit,
    sort,
  });
  const { createAuditLog } = useCreateAuditLog(setLogs);
  const { toast } = useToast();

  // Reset page when filters change
  const prevFiltersRef = React.useRef(filters);
  React.useEffect(() => {
    if (prevFiltersRef.current !== filters) {
      setPage(1);
      prevFiltersRef.current = filters;
    }
  }, [filters]);

  const handleCreateLog = async (logData) => {
    try {
      await createAuditLog(logData);
      setShowCreateModal(false);
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to create audit log",
        description: err.response?.data?.error || err.message,
      });
    }
  };

  const rows = Array.isArray(logs) ? logs : [];
  const hasActiveFilters = Object.values(filters).some(
    (v) => v !== "" && v !== undefined
  );

  /**
   * NO STATUS COLUMN, and no KPI strip above the table.
   *
   * Both described a routing experiment's lifecycle — Live / Pending /
   * Projected Uplift — on a list that is otherwise a record of completed facts.
   * "Pending" was never set by a caller; it came from a fabricated 30-day
   * observation window, so 859 finished changes (a created company, an updated
   * MID, a revealed credential) sat there reading Pending forever, each with a
   * progress bar underneath it. The KPIs summed the same fiction over one page
   * of 25. They come back when there is something real to measure.
   *
   * There is deliberately no Client column: the API returns a raw `companyId`
   * with no name resolution, and a column of UUIDs is worse than no column.
   * That needs a server-side join, not a cell.
   */
  const columns = useMemo(
    () => [
      {
        key: "title",
        header: "Change",
        width: "30%",
        cell: (log) => (
          <span className="font-medium">
            {log.title || log.entityName || "—"}
          </span>
        ),
        exportValue: (log) => log.title || log.entityName || "",
      },
      {
        key: "changeType",
        header: "Type",
        width: "17%",
        sortKey: "changeType",
        cell: (log) =>
          log.changeType ? (
            <span className="badge badge-muted">{log.changeType}</span>
          ) : (
            // A blank here is a WRITER BUG, not missing data — every call site
            // supplies a type, so an empty one means the payload never arrived.
            <span className="text-muted-foreground">—</span>
          ),
        exportValue: (log) => log.changeType || "",
      },
      {
        key: "changeCategory",
        header: "Category",
        width: "11%",
        sortKey: "changeCategory",
        headerClassName: "hidden lg:table-cell",
        className: "hidden lg:table-cell",
        cell: (log) =>
          log.changeCategory ? (
            <span className={`badge ${categoryBadgeClass(log.changeCategory)}`}>
              {log.changeCategory}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
        exportValue: (log) => log.changeCategory || "",
      },
      {
        key: "description",
        header: "Detail",
        width: "18%",
        headerClassName: "hidden xl:table-cell",
        className: "hidden xl:table-cell text-muted-foreground",
        cell: (log) => log.change?.description || "—",
        exportValue: (log) => log.change?.description || "",
      },
      {
        key: "implementer",
        header: "By",
        width: "13%",
        cell: (log) => log.implementer?.name || "—",
        exportValue: (log) => log.implementer?.name || "",
      },
      {
        key: "createdAt",
        header: "When",
        width: "11%",
        sortKey: "createdAt",
        defaultSortDir: "desc",
        className: "text-muted-foreground",
        cell: (log) => <RelativeTime value={log.createdAt} />,
        exportValue: (log) => log.createdAt || "",
      },
    ],
    []
  );

  return (
    <div>
      <PageHeader
        title="Change Log"
        subtitle="System and user-initiated changes across your environment."
        meta={[
          { label: "Total", value: (pagination.total || 0).toLocaleString() },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <button className="btn btn-secondary" onClick={() => setFiltersOpen(true)}>
              <Filter className="h-4 w-4" />
              Filters
              {hasActiveFilters && (
                <span className="badge badge-brand ml-1">
                  {
                    Object.values(filters).filter(
                      (v) => v !== "" && v !== undefined
                    ).length
                  }
                </span>
              )}
            </button>
            <button
              className="btn btn-secondary"
              disabled={!rows.length}
              onClick={() =>
                downloadCsv(csvFilename("change-log"), toCsv(rows, columns))
              }
            >
              <Download className="h-4 w-4" />
              Export
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary"
            >
              <Plus className="h-4 w-4" />
              Create Log
            </button>
          </div>
        }
      />

      <div className="container space-y-6 py-6">
        <ListSearchBar
          value={filters.search || ""}
          onChange={(v) => set("search", v)}
          placeholder="Search change, entity or description"
        >
          <select
            className="select w-auto"
            value={filters.changeCategory || ""}
            onChange={(e) => set("changeCategory", e.target.value)}
            aria-label="Category"
          >
            <option value="">Category: Any</option>
            {Object.values(ChangeCategories).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </ListSearchBar>

        <RecordTable
          columns={columns}
          rows={rows}
          rowKey={(log) => log.logId || log._id}
          onRowClick={(log) => log.logId && navigate(`/audit/${log.logId}`)}
          loading={loading}
          error={error}
          onRetry={refetch}
          filtered={hasActiveFilters}
          sort={sort}
          onSortChange={(next) => {
            setSort(next);
            setPage(1);
          }}
          noMatch={{ icon: ScrollText, heading: "No matching log entries" }}
          empty={{
            icon: ScrollText,
            heading: "No log entries found",
            description: "Changes are recorded here as they happen.",
            errorText: "Failed to load the change log.",
          }}
        />

        <ListPagination
          page={pagination.page || page}
          totalPages={pagination.totalPages || 0}
          total={pagination.total || 0}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      </div>

      <SearchPanel
        namespace="auditLog"
        title="Filter Change Log"
        isExpanded={filtersOpen}
        onClose={() => setFiltersOpen(false)}
      />

      <CreateLogModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateLog}
      />
    </div>
  );
}

function Audit() {
  return (
    <SearchProvider namespace="auditLog">
      <AuditInner />
    </SearchProvider>
  );
}

export default Audit;
